import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { dataCache, CACHE_TTL } from './cache/dataCache';
import { CACHE_KEYS } from './cache/cacheKeys';

const FIRESTORE_IN_LIMIT = 10;

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export class BarcodeLookupService {
  private static getKnownBarcodes(): Set<string> {
    const cached = dataCache.get<string[]>(CACHE_KEYS.barcodeExists, CACHE_TTL.barcodePositive);
    return new Set(cached ?? []);
  }

  private static saveKnownBarcodes(barcodes: Set<string>): void {
    dataCache.set(CACHE_KEYS.barcodeExists, Array.from(barcodes));
  }

  static markAsExisting(barkod: string): void {
    const known = this.getKnownBarcodes();
    known.add(barkod);
    this.saveKnownBarcodes(known);
  }

  static markManyAsExisting(barcodes: string[]): void {
    const known = this.getKnownBarcodes();
    barcodes.forEach((b) => known.add(b));
    this.saveKnownBarcodes(known);
  }

  /** Sadece cache'te olmayan barkodlar için Firebase sorgusu yapar */
  static async checkExistingBarcodes(barcodes: string[]): Promise<Set<string>> {
    const known = this.getKnownBarcodes();
    const uncached = barcodes.filter((b) => !known.has(b));

    if (uncached.length > 0) {
      const productsRef = collection(db, 'products');
      const chunks = chunkArray(uncached, FIRESTORE_IN_LIMIT);

      for (const chunk of chunks) {
        const q = query(productsRef, where('barkod', 'in', chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          const barkod = docSnap.data().barkod;
          if (barkod) known.add(barkod);
        });
      }

      this.saveKnownBarcodes(known);
    }

    return new Set(barcodes.filter((b) => known.has(b)));
  }

  static async isBarcodeExisting(barkod: string): Promise<boolean> {
    const existing = await this.checkExistingBarcodes([barkod]);
    return existing.has(barkod);
  }
}
